"use client";

import * as React from "react";

export type DocsFakiMode = "floating" | "immersed";

const STORAGE_MODE = "orkestria-docs-faki-mode";
const STORAGE_WIDTH = "orkestria-docs-faki-width";
const STORAGE_OPEN = "orkestria-docs-faki-open";
const MIN_W = 320;
const MAX_W = 720;
const DEFAULT_W = 420;

type Ctx = {
  open: boolean;
  setOpen: (v: boolean) => void;
  mode: DocsFakiMode;
  setMode: (m: DocsFakiMode) => void;
  width: number;
  setWidth: (n: number) => void;
};

const DocsFakiContext = React.createContext<Ctx | null>(null);

export function DocsFakiProvider({ children }: { children: React.ReactNode }) {
  // Defaults: panel abierto + modo inmersivo (en desktop empuja el contenido,
  // en móvil cae a flotante por viewport). Si el usuario cierra explícitamente,
  // se persiste y no se reabre solo en próximas cargas.
  const [open, setOpenState] = React.useState(true);
  const [mode, setModeState] = React.useState<DocsFakiMode>("immersed");
  const [width, setWidthState] = React.useState<number>(DEFAULT_W);
  const [hydrated, setHydrated] = React.useState(false);

  React.useEffect(() => {
    try {
      const m = window.localStorage.getItem(STORAGE_MODE);
      if (m === "immersed" || m === "floating") setModeState(m);
      const w = Number(window.localStorage.getItem(STORAGE_WIDTH));
      if (Number.isFinite(w) && w >= MIN_W && w <= MAX_W) setWidthState(w);

      const o = window.localStorage.getItem(STORAGE_OPEN);
      if (o === "false") {
        setOpenState(false);
      } else if (o === "true") {
        setOpenState(true);
      } else {
        // Sin valor explícito: abierto en desktop, cerrado en móvil
        // (≥1024px = desktop). No persiste — solo si el usuario interactúa.
        const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
        setOpenState(isDesktop);
      }
    } catch {}
    setHydrated(true);
  }, []);

  const setOpen = React.useCallback((v: boolean) => {
    setOpenState(v);
    try {
      window.localStorage.setItem(STORAGE_OPEN, v ? "true" : "false");
    } catch {}
  }, []);

  const setMode = React.useCallback((m: DocsFakiMode) => {
    setModeState(m);
    try {
      window.localStorage.setItem(STORAGE_MODE, m);
    } catch {}
  }, []);

  const setWidth = React.useCallback((n: number) => {
    const clamped = Math.max(MIN_W, Math.min(MAX_W, n));
    setWidthState(clamped);
    try {
      window.localStorage.setItem(STORAGE_WIDTH, String(clamped));
    } catch {}
  }, []);

  // Evitar SSR mismatch — no exponer hasta hidratar
  if (!hydrated) {
    return (
      <DocsFakiContext.Provider
        value={{
          open: false,
          setOpen: () => {},
          mode: "floating",
          setMode: () => {},
          width: DEFAULT_W,
          setWidth: () => {},
        }}
      >
        {children}
      </DocsFakiContext.Provider>
    );
  }

  return (
    <DocsFakiContext.Provider value={{ open, setOpen, mode, setMode, width, setWidth }}>
      {children}
    </DocsFakiContext.Provider>
  );
}

export function useDocsFaki() {
  const ctx = React.useContext(DocsFakiContext);
  if (!ctx) throw new Error("useDocsFaki must be used inside DocsFakiProvider");
  return ctx;
}

export const DOCS_FAKI_WIDTH = { MIN_W, MAX_W, DEFAULT_W };
